import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from '@/components/Select';
import { apiPutRequest } from '@/utils/apiClient';
import { useUser } from '@/hooks/UserContext';

const TimeZonePreference = (): ReactElement | null => {
    const { userInfo } = useUser();
    const { t } = useTranslation('settings');

    const [timeZone, setTimeZone] = useState<string>('');
    const [error, setError] = useState<string>('');

    // 初期値: ユーザー情報のTZ（なければlocalStorageのキャッシュ）
    useEffect(() => {
        if (userInfo?.timeZone) {
            setTimeZone(userInfo.timeZone);
            return;
        }
        try {
            const raw = localStorage.getItem('kotomil_user_info');
            if (raw) {
                const obj = JSON.parse(raw);
                if (obj?.timeZone) setTimeZone(obj.timeZone as string);
            }
        } catch {}
    }, [userInfo?.timeZone]);

    const timeZoneIds = useMemo<string[]>(() => {
        if (typeof (Intl as any).supportedValuesOf === 'function') {
            return (Intl as any).supportedValuesOf('timeZone') as string[];
        }
        return [];
    }, []);

    const tzOptions = useMemo(() => {
        // 表示は英語に統一
        const displayLocale = 'en-US';
        let tzDisplay: Intl.DisplayNames | null = null;
        try {
            tzDisplay = new (Intl as any).DisplayNames([displayLocale], { type: 'timezone' });
        } catch {
            tzDisplay = null;
        }
        const now = new Date();

        const labelFor = (tz: string): { label: string; sortKey: string } => {
            // 英語: DisplayNames優先 → DateTimeFormat(short) → フォールバック
            if (tzDisplay) {
                const name = (tzDisplay as any).of(tz) as string;
                if (name && typeof name === 'string') {
                    return { label: `${name} (${tz})`, sortKey: name };
                }
            }
            try {
                const parts = new Intl.DateTimeFormat(displayLocale, {
                    timeZone: tz,
                    timeZoneName: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                }).formatToParts(now);
                const tzName = parts.find((p) => p.type === 'timeZoneName')?.value || '';
                if (tzName) {
                    return { label: `${tz} (${tzName})`, sortKey: tz };
                }
            } catch {}
            // 3) 最終フォールバック
            return { label: tz, sortKey: tz };
        };

        const unsorted = timeZoneIds.map((tz) => {
            const { label, sortKey } = labelFor(tz);
            return { value: tz, label, sortKey } as {
                value: string;
                label: string;
                sortKey: string;
            };
        });

        const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });
        unsorted.sort((a, b) => collator.compare(a.sortKey, b.sortKey));

        return unsorted.map(({ value, label }) => ({ value, label }));
    }, [timeZoneIds]);

    const selectedOption = useMemo(() => {
        if (!timeZone) return { value: '', label: '' };
        const found = tzOptions.find((o) => o.value === timeZone);
        if (found) return found;
        return { value: timeZone, label: timeZone };
    }, [timeZone, tzOptions]);

    const saveTimeZone = async (tz: string) => {
        setError('');
        try {
            // IANA検証（フロント）
            try {
                // validate IANA time zone
                // eslint-disable-next-line no-new
                new Intl.DateTimeFormat(undefined, { timeZone: tz });
            } catch {
                setError('Invalid time zone');
                return;
            }
            const res = await apiPutRequest('/api/user/time-zone', { timeZone: tz });
            if (!res.ok) {
                setError('Invalid time zone');
                return;
            }
            // 変更を即時反映するためにページを再読み込み
            window.location.reload();
        } catch {
            setError('Invalid time zone');
        }
    };

    return (
        <section className="flex flex-col gap-3">
            <h3 className="text-lg font-medium">{t('timezone.heading')}</h3>
            <p className="text-sm text-gray-600">{t('timezone.description')}</p>
            <div className="max-w-md">
                <Select
                    options={tzOptions}
                    defaultValue={selectedOption}
                    onValueChange={async (opt) => {
                        setTimeZone(opt.value);
                        await saveTimeZone(opt.value);
                    }}
                />
                {/* 成功/進行テキストは不要。エラーのみ最低限表示 */}
                {error && (
                    <p className="text-red-600 text-sm mt-1">{error || t('timezone.error')}</p>
                )}
            </div>
        </section>
    );
};

export default TimeZonePreference;
